import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromDrupalData from './drupal-data.reducer';
import { drupalDatasFeatureKey } from './drupal-data.reducer';
import { Dictionary } from '@ngrx/entity';
import { DrupalData } from './drupal-data.model';
import { JsonApiQuery } from '../json-api.query';
import { DrupalIdentifier } from '@nx-icilalune/drupal-types';

export const selectDrupalDataState =
  createFeatureSelector<fromDrupalData.State>(drupalDatasFeatureKey);

export const selectLanguage = createSelector(
  selectDrupalDataState,
  (state) => state.lang
);

export const selectAllDrupalData = createSelector(
  selectDrupalDataState,
  fromDrupalData.selectAllDrupalData
);

export const selectAllDrupalLanguageData = createSelector(
  selectAllDrupalData,
  selectLanguage,
  (drupalData, lang) => drupalData.filter((d) => d.language === lang)
);

export const selectAllNodes = createSelector(
  selectAllDrupalLanguageData,
  (drupalData) => {
    return drupalData.filter((d) => d.type.indexOf('node--') > -1);
  }
);

export const selectAllTerms = createSelector(
  selectAllDrupalLanguageData,
  (drupalData) => {
    return drupalData.filter((d) => d.type.indexOf('taxonomy_term--') > -1);
  }
);

export const selectAllMedia = createSelector(
  selectAllDrupalLanguageData,
  (drupalData) => {
    return drupalData.filter((d) => {
      return (
        d.type.indexOf('images') > -1 ||
        d.type.indexOf('svgs') > -1 ||
        d.type.indexOf('vimeo') > -1
      );
    });
  }
);

export const selectDrupalDataEntities = createSelector(
  selectDrupalDataState,
  fromDrupalData.selectDrupalDataEntities
);

export const selectEntityById = (id: string) =>
  createSelector(selectAllDrupalLanguageData, (drupalData) => {
    if (id) {
      return drupalData.find((d) => d.id === id);
    }
    return null;
  });

export const selectEntitiesById = (ids: string[] | undefined) =>
  createSelector(selectAllDrupalLanguageData, (drupalData) => {
    if (ids) {
      return ids.map((id) => {
        return drupalData.find((d) => id.indexOf(d.id) > -1);
      });
    }
    return null;
  });

export const selectEntity = (drupalIdentifier: DrupalIdentifier) =>
  createSelector(
    selectDrupalDataEntities,
    selectLanguage,
    (entities: Dictionary<DrupalData>, language) => {
      const { type, id } = drupalIdentifier;
      return entities[`${language}*${type}*${id}`];
    }
  );

export const selectEntities = (drupalIdentifiers: DrupalIdentifier[]) =>
  createSelector(
    selectDrupalDataEntities,
    selectLanguage,
    (entities: Dictionary<DrupalData>, language) => {
      return drupalIdentifiers.map((de) => {
        const { type, id } = de;
        return entities[`${language}*${type}*${id}`];
      });
    }
  );

export const selectEntitiesByType = <T extends DrupalData>(type: string) =>
  createSelector(
    selectDrupalDataEntities,
    selectLanguage,
    (entities: Dictionary<DrupalData>, language) => {
      const selectedEntities = Object.keys(entities)
        .filter((key) => {
          const entityType = entities[key]?.type;
          if (entityType) {
            return (
              entityType.indexOf(type) > -1 && key.indexOf(`${language}*`) > -1
            );
          }
          return undefined;
        })
        .map((key) => {
          if (key) {
            return entities[key];
          }
          return undefined;
        })
        .filter((d) => !!d);
      return selectedEntities as T[];
    }
  );

export const selectMenus = createSelector(selectDrupalDataState, (state) => {
  return state.menus;
});

export const selectLanguageMenus = createSelector(
  selectLanguage,
  selectMenus,
  (language, menus) => {
    return language ? menus[language] : null;
  }
);

export const selectMenu = (id: string) =>
  createSelector(selectLanguageMenus, (menus) => {
    return menus?.[id];
  });

export const selectMainMenu = createSelector(selectLanguageMenus, (menus) => {
  return menus?.main;
});

export const selectTypologiesMenu = createSelector(
  selectLanguageMenus,
  (menus) => {
    return menus?.typologies;
  }
);

export const selectRequests = createSelector(selectDrupalDataState, (state) => {
  return state.requests;
});

export const selectLanguageRequests = createSelector(
  selectLanguage,
  selectRequests,
  (language, requests) => {
    return language ? requests[language] : null;
  }
);

export const selectRequestById = (queryId: string | undefined) =>
  createSelector(selectLanguageRequests, (requests) => {
    if (queryId) {
      return requests?.[queryId];
    }
    return undefined;
  });

export const selectRequestIsComplete = (queryId: string | undefined) =>
  createSelector(selectRequestById(queryId), (request) => {
    return request?.complete;
  });

export const selectRequestByIds = (queryIds: string[]) =>
  createSelector(selectLanguageRequests, (requests): JsonApiQuery[] => {
    if (requests && queryIds) {
      return queryIds
        .map((queryId) => {
          return requests[queryId];
        })
        .filter((query) => !!query);
    }
    return [];
  });
